import { useCallback, useMemo, useState } from "react"

import { type MetadataType } from "@/constants/metadata-types"

import type { ConversationMetricRule } from "@/types/api/ConversationMetricRule"

import {
  applyConversationMetricRuleMatchType,
  applyConversationMetricRuleMetadataType,
  canSubmitConversationMetricRule,
  copyConversationMetricRule,
  createConversationMetricRuleConditionDraft,
  createConversationMetricRuleDraft,
  createConversationMetricRuleDraftFromRule,
  createConversationMetricRuleFromDraft,
  removeConversationMetricRule,
  replaceConversationMetricRule,
} from "../utils/conversationMetricsConfigurationRules"

type MetadataCondition = ReturnType<
  typeof createConversationMetricRuleConditionDraft
>

interface UseConversationMetricRuleEditorParams {
  createId?: () => string
  rules: ConversationMetricRule[]
  setRules: (rules: ConversationMetricRule[]) => void
}

export function useConversationMetricRuleEditor({
  createId = () => Date.now().toString(),
  rules,
  setRules,
}: UseConversationMetricRuleEditorParams) {
  const [draft, setDraft] = useState(createConversationMetricRuleDraft())
  const [editingRule, setEditingRule] = useState<ConversationMetricRule | null>(
    null,
  )
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false)
  const [isEditRuleModalOpen, setIsEditRuleModalOpen] = useState(false)

  const handleAddRuleModalOpen = useCallback(() => {
    setIsAddRuleModalOpen(true)
  }, [])

  const handleAddRuleModalClose = useCallback(() => {
    setIsAddRuleModalOpen(false)
  }, [])

  const handleToggleRuleMetric = useCallback(
    (metricName: string, isSelected: boolean) => {
      if (isSelected) {
        setDraft((previous) => ({
          ...previous,
          metrics: [...previous.metrics, metricName],
        }))
        return
      }

      setDraft((previous) => ({
        ...previous,
        metrics: previous.metrics.filter((id) => id !== metricName),
      }))
    },
    [],
  )

  const setSelectedRuleType = useCallback((type: "RUN_METADATA") => {
    setDraft((previous) => ({
      ...previous,
      type,
    }))
  }, [])

  const setSelectedMetrics = useCallback((metrics: string[]) => {
    setDraft((previous) => ({
      ...previous,
      metrics,
    }))
  }, [])

  const updateMetadataCondition = useCallback(
    (
      conditionIndex: number,
      updateCondition: (condition: MetadataCondition) => MetadataCondition,
    ) => {
      setDraft((previous) => ({
        ...previous,
        metadataConditions: previous.metadataConditions.map(
          (condition, index) =>
            index === conditionIndex ? updateCondition(condition) : condition,
        ),
      }))
    },
    [],
  )

  const setMetadataConditionKey = useCallback(
    (conditionIndex: number, metadataKey: string) => {
      updateMetadataCondition(conditionIndex, (condition) => ({
        ...condition,
        metadataKey,
      }))
    },
    [updateMetadataCondition],
  )

  const handleMetadataConditionTypeChange = useCallback(
    (conditionIndex: number, metadataType: MetadataType) => {
      setDraft((previous) =>
        applyConversationMetricRuleMetadataType(
          previous,
          metadataType,
          conditionIndex,
        ),
      )
    },
    [],
  )

  const setMetadataConditionMatchType = useCallback(
    (
      conditionIndex: number,
      matchType: ConversationMetricRule["matchType"],
    ) => {
      setDraft((current) =>
        applyConversationMetricRuleMatchType(
          current,
          matchType,
          conditionIndex,
        ),
      )
    },
    [],
  )

  const setMetadataConditionValue = useCallback(
    (
      conditionIndex: number,
      metadataValue: string | boolean | number | string[],
    ) => {
      updateMetadataCondition(conditionIndex, (condition) => ({
        ...condition,
        metadataValue,
      }))
    },
    [updateMetadataCondition],
  )

  const handleMetadataConditionBooleanValueChange = useCallback(
    (conditionIndex: number, value: string) => {
      const isTrue = value === "true"
      updateMetadataCondition(conditionIndex, (condition) => ({
        ...condition,
        matchType: isTrue ? "IS_TRUE" : "IS_FALSE",
        metadataValue: isTrue,
      }))
    },
    [updateMetadataCondition],
  )

  const addMetadataCondition = useCallback(() => {
    setDraft((previous) => ({
      ...previous,
      metadataConditions: [
        ...previous.metadataConditions,
        createConversationMetricRuleConditionDraft(),
      ],
    }))
  }, [])

  const removeMetadataCondition = useCallback((conditionIndex: number) => {
    setDraft((previous) => {
      if (previous.metadataConditions.length <= 1) {
        return previous
      }

      return {
        ...previous,
        metadataConditions: previous.metadataConditions.filter(
          (_condition, index) => index !== conditionIndex,
        ),
      }
    })
  }, [])

  const addStringArrayMetadataConditionValue = useCallback(
    (conditionIndex: number) => {
      updateMetadataCondition(conditionIndex, (condition) => {
        const values = Array.isArray(condition.metadataValue)
          ? condition.metadataValue
          : []

        return {
          ...condition,
          metadataValue: [...values, ""],
        }
      })
    },
    [updateMetadataCondition],
  )

  const updateStringArrayMetadataConditionValue = useCallback(
    (conditionIndex: number, valueIndex: number, value: string) => {
      updateMetadataCondition(conditionIndex, (condition) => {
        const nextValues = Array.isArray(condition.metadataValue)
          ? [...condition.metadataValue]
          : []
        nextValues[valueIndex] = value

        return {
          ...condition,
          metadataValue: nextValues,
        }
      })
    },
    [updateMetadataCondition],
  )

  const removeStringArrayMetadataConditionValue = useCallback(
    (conditionIndex: number, valueIndex: number) => {
      updateMetadataCondition(conditionIndex, (condition) => {
        const nextValues = Array.isArray(condition.metadataValue)
          ? [...condition.metadataValue]
          : []
        nextValues.splice(valueIndex, 1)

        return {
          ...condition,
          metadataValue: nextValues,
        }
      })
    },
    [updateMetadataCondition],
  )

  const handleSaveRule = useCallback(() => {
    const newRule = createConversationMetricRuleFromDraft({
      draft,
      id: createId(),
    })

    setRules([...rules, newRule])
    setDraft(createConversationMetricRuleDraft())
    setIsAddRuleModalOpen(false)
  }, [createId, draft, rules, setRules])

  const handleDeleteRule = useCallback(
    (ruleId: string) => {
      setRules(removeConversationMetricRule(rules, ruleId))
    },
    [rules, setRules],
  )

  const handleEditRule = useCallback((rule: ConversationMetricRule) => {
    setEditingRule(rule)
    setDraft(createConversationMetricRuleDraftFromRule(rule))
    setIsEditRuleModalOpen(true)
  }, [])

  const handleUpdateRule = useCallback(() => {
    if (!editingRule) {
      return
    }

    const updatedRule = {
      ...editingRule,
      ...createConversationMetricRuleFromDraft({
        draft,
        id: editingRule.id,
      }),
    }

    setRules(replaceConversationMetricRule(rules, updatedRule))
    setEditingRule(null)
    setDraft(createConversationMetricRuleDraft())
    setIsEditRuleModalOpen(false)
  }, [draft, editingRule, rules, setRules])

  const handleCloseEditModal = useCallback(() => {
    setEditingRule(null)
    setDraft(createConversationMetricRuleDraft())
    setIsEditRuleModalOpen(false)
  }, [])

  const handleCopyRule = useCallback(
    (rule: ConversationMetricRule) => {
      setRules([...rules, copyConversationMetricRule({ id: createId(), rule })])
    },
    [createId, rules, setRules],
  )

  const selectedRuleType = draft.type
  const metadataConditions = draft.metadataConditions
  const firstMetadataCondition =
    metadataConditions[0] ?? createConversationMetricRuleConditionDraft()
  const metadataKey = firstMetadataCondition.metadataKey
  const metadataType = firstMetadataCondition.metadataType
  const matchType = firstMetadataCondition.matchType
  const metadataValue = firstMetadataCondition.metadataValue
  const selectedMetrics = draft.metrics
  const canSubmitRule = useMemo(
    () => canSubmitConversationMetricRule(draft, editingRule ?? undefined),
    [draft, editingRule],
  )

  const setMetadataKey = useCallback(
    (metadataKey: string) => {
      setMetadataConditionKey(0, metadataKey)
    },
    [setMetadataConditionKey],
  )

  const handleMetadataTypeChange = useCallback(
    (metadataType: MetadataType) => {
      handleMetadataConditionTypeChange(0, metadataType)
    },
    [handleMetadataConditionTypeChange],
  )

  const setMatchType = useCallback(
    (matchType: ConversationMetricRule["matchType"]) => {
      setMetadataConditionMatchType(0, matchType)
    },
    [setMetadataConditionMatchType],
  )

  const setMetadataValue = useCallback(
    (metadataValue: string | boolean | string[]) => {
      setMetadataConditionValue(0, metadataValue)
    },
    [setMetadataConditionValue],
  )

  const handleBooleanValueChange = useCallback(
    (value: string) => {
      handleMetadataConditionBooleanValueChange(0, value)
    },
    [handleMetadataConditionBooleanValueChange],
  )

  const addStringArrayMetadataValue = useCallback(() => {
    addStringArrayMetadataConditionValue(0)
  }, [addStringArrayMetadataConditionValue])

  const updateStringArrayMetadataValue = useCallback(
    (index: number, value: string) => {
      updateStringArrayMetadataConditionValue(0, index, value)
    },
    [updateStringArrayMetadataConditionValue],
  )

  const removeStringArrayMetadataValue = useCallback(
    (index: number) => {
      removeStringArrayMetadataConditionValue(0, index)
    },
    [removeStringArrayMetadataConditionValue],
  )

  return {
    addMetadataCondition,
    addStringArrayMetadataValue,
    addStringArrayMetadataConditionValue,
    canSubmitRule,
    handleAddRuleModalClose,
    handleAddRuleModalOpen,
    handleBooleanValueChange,
    handleMetadataConditionBooleanValueChange,
    handleCloseEditModal,
    handleCopyRule,
    handleDeleteRule,
    handleEditRule,
    handleMetadataConditionTypeChange,
    handleMetadataTypeChange,
    handleSaveRule,
    handleToggleRuleMetric,
    handleUpdateRule,
    isAddRuleModalOpen,
    isEditRuleModalOpen,
    matchType,
    metadataConditions,
    metadataKey,
    metadataType,
    metadataValue,
    removeMetadataCondition,
    removeStringArrayMetadataConditionValue,
    selectedMetrics,
    selectedRuleType,
    setMetadataConditionKey,
    setMetadataConditionMatchType,
    setMetadataConditionValue,
    setMatchType,
    setMetadataKey,
    setMetadataValue,
    setSelectedMetrics,
    setSelectedRuleType,
    updateStringArrayMetadataConditionValue,
    updateStringArrayMetadataValue,
    removeStringArrayMetadataValue,
  }
}
